import React, { useState } from "react";
import { Modal } from "@mantine/core";
import { useApp } from "../../context/AppContext";
import { useSalon } from "../../context/SalonContext";
import { useResponsive } from "../../context/ResponsiveContext";
import { CreateTaxRateRequest, TaxRate } from "../../types/taxRates";
import TaxRateForm from "./TaxRateForm";

interface CreateTaxRateModalProps {
  opened: boolean;
  onClose: () => void;
  /** Called with the newly created rate (from POST /tax-rates) on success. */
  onCreated: (rate: TaxRate) => void;
}

/**
 * Lightweight "create a tax rate" dialog for inline flows such as adding a
 * product, where the user discovers mid-task that the rate they need doesn't
 * exist yet. Reuses TaxRateForm without its product/service pickers — the
 * caller links the new rate to whatever it is creating.
 *
 * Opens full-screen on mobile so the date pickers and buttons stay reachable
 * when stacked on top of another modal.
 */
export default function CreateTaxRateModal({
  opened,
  onClose,
  onCreated,
}: CreateTaxRateModalProps) {
  const { createTaxRate } = useApp();
  const { businessInfo } = useSalon() as {
    businessInfo: { id: string } | null;
  };
  const { isMobile } = useResponsive();
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleClose = () => {
    if (isSaving) return;
    setSaveError(null);
    onClose();
  };

  const handleSubmit = async (data: CreateTaxRateRequest) => {
    if (!businessInfo?.id) {
      setSaveError("No location selected. Reload the page and try again.");
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    try {
      const created: TaxRate = await createTaxRate(businessInfo.id, data);
      onCreated(created);
      onClose();
    } catch (err: any) {
      console.error("Failed to create tax rate:", err);
      setSaveError(err?.message || "Failed to create tax rate");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="New Tax Rate"
      size="lg"
      fullScreen={isMobile}
      closeOnClickOutside={false}
      closeOnEscape={!isSaving}
    >
      <TaxRateForm
        initialRate={null}
        showAssignments={false}
        onSubmit={(data) => handleSubmit(data as CreateTaxRateRequest)}
        onCancel={handleClose}
        isSaving={isSaving}
        saveError={saveError}
      />
    </Modal>
  );
}
